/**
 * AnchorBanner - Collapsible banner showing key dates from plan facts.
 *
 * Shows dates like "Next billing date: Jan 17" to give context for scheduling.
 */

import React, { useState } from 'react';
import { Box, Typography, IconButton, Collapse } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { colors } from '../../../theme/colors';

interface AnchorBannerProps {
  anchors: Map<string, Date>;
}

/**
 * Format anchor key for display (e.g., "nextBillingDate" -> "Next billing date").
 */
function formatAnchorKey(key: string): string {
  // Convert camelCase to sentence case
  const result = key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .replace(/date/i, '')
    .trim();

  return result;
}

/**
 * Format date for display.
 */
function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export const AnchorBanner: React.FC<AnchorBannerProps> = ({ anchors }) => {
  const [expanded, setExpanded] = useState(true);

  // Convert anchors to array for rendering
  const anchorEntries = Array.from(anchors.entries());

  if (anchorEntries.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mb: 2,
        borderRadius: 1.5,
        bgcolor: colors.blue[50],
        border: `1px solid ${colors.blue[100]}`,
        overflow: 'hidden',
      }}
    >
      {/* Header - always visible */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: colors.blue[100],
          },
        }}
      >
        <CalendarTodayIcon
          sx={{ fontSize: 16, color: colors.blue[600] }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: colors.blue[700],
            fontSize: '0.8rem',
            flex: 1,
          }}
        >
          Key dates
        </Typography>
        <IconButton
          size="small"
          sx={{ p: 0.25 }}
        >
          {expanded ? (
            <ExpandLessIcon sx={{ fontSize: 18, color: colors.blue[600] }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 18, color: colors.blue[600] }} />
          )}
        </IconButton>
      </Box>

      {/* Collapsible content */}
      <Collapse in={expanded}>
        <Box
          sx={{
            px: 2,
            pb: 1.5,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {anchorEntries.map(([key, date]) => (
            <Box
              key={key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: colors.blue[600],
                  fontSize: '0.7rem',
                }}
              >
                {formatAnchorKey(key)}:
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: colors.blue[700],
                  fontSize: '0.75rem',
                }}
              >
                {formatDate(date)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AnchorBanner;
